import React, { useEffect, useState } from "react";
import { Loader2, Search, ShieldAlert, Flame, Vault, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  getReserveStats,
  getReserveWallets,
  getBurnLedger,
  triggerEmergencySignal,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function StatTile({ label, value, sub, tone = "zinc", testid }) {
  const tones = {
    zinc: "border-zinc-900 text-zinc-100",
    amber: "border-amber-500/30 text-amber-300",
    rose: "border-rose-500/30 text-rose-300",
    emerald: "border-emerald-500/30 text-emerald-200",
  };
  return (
    <div className={`border ${tones[tone]} bg-zinc-950/60 p-4`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">{label}</div>
      <div className="text-2xl font-light tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function fmt(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString();
}

export default function Reserve() {
  const { user } = useAuth();
  const isArchitect = !!user?.is_architect;
  const [stats, setStats] = useState(null);
  const [wallets, setWallets] = useState(null);
  const [burns, setBurns] = useState(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showEmergency, setShowEmergency] = useState(false);
  const [password, setPassword] = useState("");
  const [signalBusy, setSignalBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, w, b] = await Promise.all([
        getReserveStats(),
        getReserveWallets(page, 100, q),
        getBurnLedger(50),
      ]);
      setStats(s);
      setWallets(w);
      setBurns(b);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    refresh();
  };

  const fireSignal = async () => {
    if (!password.trim()) {
      toast.error("Password required");
      return;
    }
    if (!window.confirm("Fire the Emergency Signal? Wrong password will permanently freeze your account.")) return;
    setSignalBusy(true);
    try {
      const r = await triggerEmergencySignal(password);
      toast.success("Emergency Signal accepted. Reserve unlocked.");
      setPassword("");
      setShowEmergency(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Emergency Signal rejected.");
    } finally {
      setSignalBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="reserve-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.35em] text-amber-300 mb-2 inline-flex items-center gap-2">
            <Vault className="h-3 w-3" /> humanitarian reserve · v18.99
          </div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight">
            22,000 locked wallets, held in trust.
          </h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
            Every verified truth deposits 51% of its AEGIS mint into one of these
            wallets. There are no logins for any of them. The coins held here
            are publicly visible but cannot move until the Architect triggers
            the Emergency Signal — at which point all 22,000 wallets unlock at
            once for humanitarian distribution. Verifiers receive 35%, the
            Architect 9%, and 5% of every mint is permanently burned for
            supply deflation.
          </p>
        </div>

        {loading && !stats && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading reserve…
          </div>
        )}

        {stats && (
          <div className="grid sm:grid-cols-4 gap-3 mb-6">
            <StatTile
              label="Reserve wallets"
              value={fmt(stats.wallet_count)}
              sub={`expected ${fmt(stats.expected_wallet_count)}`}
              tone="amber"
              testid="reserve-wallet-count"
            />
            <StatTile
              label="Funded wallets"
              value={fmt(stats.funded_wallet_count)}
              sub={`${fmt(stats.coin_count)} coins held`}
              testid="reserve-funded-count"
            />
            <StatTile
              label="Total APT locked"
              value={Number(stats.total_apt || 0).toFixed(2)}
              sub={`${fmt(stats.total_proof)} PROOF`}
              tone="emerald"
              testid="reserve-apt-total"
            />
            <StatTile
              label="Emergency status"
              value={stats.emergency_unlocked ? "UNLOCKED" : "Sealed"}
              sub={
                stats.signal_attempt_count
                  ? `${stats.signal_attempt_count} signal attempts logged`
                  : "no attempts logged"
              }
              tone={stats.emergency_unlocked ? "rose" : "zinc"}
              testid="reserve-emergency-status"
            />
          </div>
        )}

        {burns && (
          <div className="border border-rose-500/20 bg-gradient-to-br from-rose-500/5 to-transparent p-4 mb-6" data-testid="burn-summary">
            <div className="text-[10px] uppercase tracking-[0.3em] text-rose-300 mb-2 inline-flex items-center gap-2">
              <Flame className="h-3 w-3" /> Burn ledger · supply deflation
            </div>
            <div className="grid sm:grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-zinc-500 text-[11px] uppercase tracking-[0.25em] mb-0.5">Coins burned</div>
                <div className="text-rose-300 text-xl tabular-nums">{fmt(burns.total_coins)}</div>
              </div>
              <div>
                <div className="text-zinc-500 text-[11px] uppercase tracking-[0.25em] mb-0.5">APT burned</div>
                <div className="text-rose-300 text-xl tabular-nums">{Number(burns.total_apt_burned || 0).toFixed(4)}</div>
              </div>
              <div>
                <div className="text-zinc-500 text-[11px] uppercase tracking-[0.25em] mb-0.5">Burn rate</div>
                <div className="text-rose-300 text-xl tabular-nums">
                  {Math.round((burns.burn_pct_per_mint || 0) * 100)}% / mint
                </div>
              </div>
            </div>
          </div>
        )}

        {isArchitect && (
          <div className="border border-rose-500/40 bg-rose-500/5 p-4 mb-6" data-testid="emergency-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-rose-300 mb-1 inline-flex items-center gap-2">
                  <ShieldAlert className="h-3 w-3" /> emergency signal · architect only
                </div>
                <div className="text-xs text-zinc-300 max-w-xl leading-relaxed">
                  Unlocks all 22,000 reserve wallets for humanitarian
                  distribution. <span className="text-rose-300">A wrong password
                  permanently freezes your account by the reserve guardrail.</span>
                </div>
              </div>
              <button
                onClick={() => setShowEmergency((v) => !v)}
                data-testid="emergency-toggle"
                className="px-3 py-2 border border-rose-500/40 text-rose-300 text-[11px] uppercase tracking-[0.25em] hover:bg-rose-500/10"
              >
                {showEmergency ? "Cancel" : "Open emergency console"}
              </button>
            </div>
            {showEmergency && (
              <div className="mt-3 flex gap-2 flex-wrap">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="RESERVE_EMERGENCY_PASSWORD"
                  data-testid="emergency-password"
                  className="flex-1 min-w-[260px] bg-black border border-rose-500/30 px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:border-rose-500/60"
                />
                <button
                  onClick={fireSignal}
                  disabled={signalBusy || !password.trim()}
                  data-testid="emergency-fire"
                  className="px-4 py-2 border border-rose-500/60 bg-rose-500/10 text-rose-300 hover:bg-rose-500 hover:text-zinc-950 text-[11px] uppercase tracking-[0.25em] disabled:opacity-40"
                >
                  {signalBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" /> : <ShieldAlert className="h-3.5 w-3.5 inline mr-1.5" />}
                  {signalBusy ? "Sending…" : "Fire signal"}
                </button>
              </div>
            )}
          </div>
        )}

        <form onSubmit={onSearch} className="mb-4 flex gap-2 items-center" data-testid="reserve-search-form">
          <div className="flex items-center gap-2 bg-black border border-zinc-800 px-3 flex-1 focus-within:border-amber-500/40">
            <Search className="h-3.5 w-3.5 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="search by index (0-21999) or address prefix…"
              data-testid="reserve-search"
              className="flex-1 bg-transparent py-2 text-sm text-zinc-100 focus:outline-none font-mono"
            />
          </div>
          <button
            type="submit"
            data-testid="reserve-search-submit"
            className="px-4 py-2 border border-zinc-800 text-zinc-300 hover:border-amber-500/40 hover:text-amber-300 text-[11px] uppercase tracking-[0.25em]"
          >
            Search
          </button>
        </form>

        {wallets && (
          <div className="border border-zinc-900 bg-zinc-950 overflow-hidden" data-testid="reserve-wallets-table">
            <div className="grid grid-cols-12 px-3 py-2 border-b border-zinc-900 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              <div className="col-span-1">#</div>
              <div className="col-span-6">Address</div>
              <div className="col-span-2">Coins</div>
              <div className="col-span-3 text-right">APT locked</div>
            </div>
            {wallets.wallets.map((w) => (
              <div
                key={w.vault_id}
                className="grid grid-cols-12 px-3 py-2 border-b border-zinc-900 last:border-b-0 text-sm hover:bg-zinc-900/40"
                data-testid={`reserve-row-${w.index}`}
              >
                <div className="col-span-1 font-mono text-amber-300/80">{String(w.index).padStart(5, "0")}</div>
                <div className="col-span-6 font-mono text-zinc-300 truncate">{w.address}</div>
                <div className="col-span-2 tabular-nums text-zinc-400">{fmt(w.balance.coin_count)}</div>
                <div className="col-span-3 text-right tabular-nums text-amber-300">
                  {Number(w.balance.apt || 0).toFixed(4)}
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between px-3 py-3 text-[11px] text-zinc-500" data-testid="reserve-paginator">
              <div>
                Page <span className="text-zinc-300">{wallets.page}</span> of{" "}
                <span className="text-zinc-300">{fmt(wallets.pages)}</span> · {fmt(wallets.total)} wallets
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  data-testid="reserve-prev"
                  className="px-2 py-1 border border-zinc-800 hover:border-amber-500/40 disabled:opacity-30"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(wallets.pages, p + 1))}
                  disabled={page >= wallets.pages}
                  data-testid="reserve-next"
                  className="px-2 py-1 border border-zinc-800 hover:border-amber-500/40 disabled:opacity-30"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 text-[11px] text-zinc-600 leading-relaxed max-w-2xl">
          <ExternalLink className="h-3 w-3 inline mr-1" />
          Address derivation is deterministic from each wallet&apos;s index — the same
          index always resolves to the same display address. No private keys
          exist on any server; nobody can sign for these wallets, ever.
        </div>
      </div>
    </div>
  );
}
